/**
 * CourseCategoryService
 * Service for CourseCategory entity operations
 * Route: api/course-categories
 */
import { BaseService } from '../base/BaseService';
/**
 * CourseCategoryService
 * Handles course category CRUD operations and ModelView endpoints
 */
export class CourseCategoryService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? CourseCategoryService.DEFAULT_SERVICE_PATH,
            basePath: CourseCategoryService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ============================================================
    // Entity CRUD Operations
    // ============================================================
    /**
     * Get all course categories
     * @returns Promise resolving to array of course categories
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all course categories (raw response)
     * @returns Promise resolving to ResponseData with course categories
     */
    getAllRaw() {
        const url = this.buildUrl('');
        return this.http.get(url);
    }
    /**
     * Get course category by ID
     * @param id - Category ID
     * @returns Promise resolving to course category or null
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        return response.data ?? null;
    }
    /**
     * Get course category by ID (raw response)
     * @param id - Category ID
     * @returns Promise resolving to ResponseData with course category
     */
    getByIdRaw(id) {
        const endpoint = this.getEndpoint('getById', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create a new course category
     * @param category - Category data
     * @returns Promise resolving to created course category
     */
    async create(category) {
        const response = await this.createRaw(category);
        return response.data ?? null;
    }
    /**
     * Create a new course category (raw response)
     * @param category - Category data
     * @returns Promise resolving to ResponseData with created course category
     */
    createRaw(category) {
        const url = this.buildUrl('');
        return this.http.post(url, category);
    }
    /**
     * Update an existing course category
     * @param id - Category ID
     * @param category - Updated category data
     * @returns Promise resolving to updated course category
     */
    async update(id, category) {
        const response = await this.updateRaw(id, category);
        return response.data ?? null;
    }
    /**
     * Update an existing course category (raw response)
     * @param id - Category ID
     * @param category - Updated category data
     * @returns Promise resolving to ResponseData with updated course category
     */
    updateRaw(id, category) {
        const endpoint = this.getEndpoint('update', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, category);
    }
    /**
     * Delete a course category
     * @param id - Category ID
     * @returns Promise resolving to true if successful
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Delete a course category (raw response)
     * @param id - Category ID
     * @returns Promise resolving to ResponseData
     */
    deleteRaw(id) {
        const endpoint = this.getEndpoint('delete', `${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ============================================================
    // ModelView Endpoints
    // ============================================================
    /**
     * Get course category view by ID
     * @param id - Category ID
     * @returns Promise resolving to course category model view or null
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        return response.data ?? null;
    }
    /**
     * Get course category view by ID (raw response)
     * @param id - Category ID
     * @returns Promise resolving to ResponseData with course category model view
     */
    getViewRaw(id) {
        const endpoint = this.getEndpoint('getView', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all course category list views
     * @returns Promise resolving to array of course category model views
     */
    async listView() {
        const response = await this.listViewRaw();
        return response.data ?? [];
    }
    /**
     * Get all course category list views (raw response)
     * @returns Promise resolving to ResponseData with course category model views
     */
    listViewRaw() {
        const endpoint = this.getEndpoint('list-view', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
CourseCategoryService.DEFAULT_SERVICE_PATH = 'resources';
CourseCategoryService.BASE_PATH = 'api/course-categories';
//# sourceMappingURL=CourseCategoryService.js.map